import React, { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useP } from '../../i18n/useP';
import './CapexCharts.css';

/* ACTIFS EXISTANTS, VALEUR NETTE ET AMORTISSEMENTS (prototype d'Olivier,
   2026-09-30) : deux graphiques sous la table, une barre par année, empilée
   par compte, le total au-dessus. Rien ne se calcule ici : tout vient de la
   vue servie par le serveur. Les comptes se distinguent par des nuances de
   l'accent, du plus foncé au plus clair. */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const shade = (i, n) => `color-mix(in srgb, var(--color-accent) ${Math.round(100 - (i * 70) / Math.max(1, n - 1))}%, var(--color-surface-card))`;

export default function AssetsCharts({ posts, years, unit }) {
  const p = useP();
  const k = unit === 'chf' ? 1 : 1000;
  const fmt = (v) => num(v).toLocaleString('de-CH', { maximumFractionDigits: unit === 'chf' ? 0 : 1 });

  const build = useMemo(() => (key) => years.map((y) => {
    const row = { x: String(y), tot: 0 };
    posts.forEach((po) => {
      const v = num(po.years?.[y]?.[key]) / k;
      row[po.line_id] = v;
      row.tot += v;
    });
    return row;
  }), [posts, years, k]);
  const net = useMemo(() => build('target'), [build]);
  const amort = useMemo(() => build('amort'), [build]);
  if (!posts.length || !years.length) return null;

  const span = years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : String(years[0]);
  const chart = (data) => (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 22, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke="var(--color-border-inner)" vertical={false} />
        <XAxis dataKey="x" tick={{ fontSize: 12, fill: 'var(--color-text-muted)' }}
               axisLine={{ stroke: 'var(--color-border)' }} tickLine={false} />
        <YAxis tick={{ fontSize: 12, fill: 'var(--color-text-muted)' }} axisLine={false} tickLine={false}
               width={48} tickFormatter={fmt} />
        <Tooltip formatter={(v, id) => [fmt(v), posts.find((po) => po.line_id === id)?.label || id]}
                 labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
        {posts.map((po, i) => (
          <Bar key={po.line_id} dataKey={po.line_id} stackId="s" fill={shade(i, posts.length)} maxBarSize={72}
               isAnimationActive={false}>
            {i === posts.length - 1 && (
              <LabelList dataKey="tot" position="top" formatter={(v) => (num(v) ? fmt(v) : '')}
                         style={{ fontSize: 12, fontWeight: 600, fill: 'var(--color-navy)' }} />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
  const legend = (
    <div className="cxc-legend">
      {posts.map((po, i) => (
        <span key={po.line_id}><i style={{ background: shade(i, posts.length) }} />{po.label}</span>
      ))}
    </div>
  );
  const last = net[net.length - 1]?.tot || 0;
  const total = amort.reduce((s, d) => s + d.tot, 0);

  return (
    <div className="cxc-duo">
      <div className="cxc-card">
        <div className="cxc-card__h">
          <h3 className="cxc-card__t">{p('Valeur nette')}</h3>
          <span className="cxc-kpi"><b>{fmt(last)}</b> · {p('fin')} {years[years.length - 1]}</span>
        </div>
        {chart(net)}
        {legend}
      </div>
      <div className="cxc-card">
        <div className="cxc-card__h">
          <h3 className="cxc-card__t">{p('Amortissements')}</h3>
          <span className="cxc-kpi"><b>{fmt(total)}</b> · {span}</span>
        </div>
        {chart(amort)}
        {legend}
      </div>
    </div>
  );
}
